import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, Linkedin, Twitter, Facebook } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { offices, companyInfo } from '../../data/mockData';

const Footer = () => {
  const { currentLang, t, isRTL } = useLanguage();
  const officeList = offices[currentLang] || offices.fr;
  const headquarters = officeList[0];

  const footerLinks = {
    company: [
      { name: t.footer.links.about, href: "/about" },
      { name: t.footer.links.team, href: "/about#team" },
      { name: t.footer.links.careers, href: "/careers" },
      { name: t.footer.links.news, href: "/#news" }
    ],
    services: [
      { name: t.footer.links.distribution, href: "/about#services" },
      { name: t.footer.links.warehouse, href: "/about#warehouse" },
      { name: t.footer.links.logistics, href: "/about#logistics" },
      { name: t.footer.links.solutions, href: "/contact" }
    ],
    legal: [
      { name: t.footer.links.privacy, href: "/privacy" },
      { name: t.footer.links.terms, href: "/terms" },
      { name: t.footer.links.cookies, href: "/cookies" }
    ]
  };

  return (
    <footer className="bg-[#1a2e3d] text-white" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <img 
                src={companyInfo.logo} 
                alt="Boidy Logo" 
                className="h-12 w-auto object-contain bg-white rounded-lg p-1"
              />
            </div>
            <p className="text-gray-400 text-sm leading-relaxed mb-6">
              {t.footer.description}
            </p>
            <div className="flex gap-4">
              <a
                href="https://linkedin.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-[#9B4B8C] transition-colors"
              >
                <Linkedin className="w-5 h-5" />
              </a>
              <a
                href="https://twitter.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-[#9B4B8C] transition-colors"
              >
                <Twitter className="w-5 h-5" />
              </a>
              <a
                href="https://facebook.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-[#9B4B8C] transition-colors"
              >
                <Facebook className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Company Links */}
          <div>
            <h4 className="text-lg font-semibold mb-6">{t.footer.company}</h4>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="text-gray-400 hover:text-white transition-colors text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services Links */}
          <div>
            <h4 className="text-lg font-semibold mb-6">{t.footer.services}</h4>
            <ul className="space-y-3">
              {footerLinks.services.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="text-gray-400 hover:text-white transition-colors text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-semibold mb-6">{t.footer.legal}</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className={`w-5 h-5 text-[#9B4B8C] flex-shrink-0 mt-0.5 ${isRTL ? 'ml-2' : 'mr-0'}`} />
                <span className="text-gray-400 text-sm">
                  {headquarters.address}<br />
                  {headquarters.addressLine2}<br />
                  {headquarters.city}
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className={`w-5 h-5 text-[#9B4B8C] flex-shrink-0 ${isRTL ? 'ml-2' : 'mr-0'}`} />
                <a
                  href={`tel:${headquarters.phone}`}
                  className="text-gray-400 hover:text-white transition-colors text-sm"
                >
                  {headquarters.phone}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Mail className={`w-5 h-5 text-[#9B4B8C] flex-shrink-0 mt-0.5 ${isRTL ? 'ml-2' : 'mr-0'}`} />
                <div className="flex flex-col">
                  <a
                    href={`mailto:${headquarters.email}`}
                    className="text-gray-400 hover:text-white transition-colors text-sm"
                  >
                    {headquarters.email}
                  </a>
                  <a
                    href={`mailto:${headquarters.email2}`}
                    className="text-gray-400 hover:text-white transition-colors text-sm"
                  >
                    {headquarters.email2}
                  </a>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-400 text-sm">
              © {new Date().getFullYear()} Boidy. {t.footer.rights}
            </p>
            <div className="flex gap-6">
              {footerLinks.legal.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className="text-gray-400 hover:text-white transition-colors text-sm"
                >
                  {link.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
